import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@node-space/storybook-components/dist/Box'
import { Skeleton } from '@node-space/storybook-components/dist/SkeletonLoader'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import { Illustration, Text } from '@node-space/ui-components'
import { Description } from 'components/text/Description'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { NestedCustomerDetails } from 'types/customers'
import { isBusinessCustomer } from '../utils'
import { BusinessCustomerSidepanelView } from './BusinessCustomerSidepanelView'
import { IndividualCustomerSidePanelView } from './IndividualCustomerSidePanelView'

type SidePanelCustomerBodyProps = {
  customerId: string
}

export const SidePanelCustomerBody = ({ customerId }: SidePanelCustomerBodyProps) => {
  const { t } = useTranslation()
  const { data: customer, error, isPending } = useCustomerQuery<NestedCustomerDetails>(customerId)

  const {
    data: countries,
    isPending: isCountriesLoading,
    isError: isCountriesLoadingError,
  } = useCountriesQuery(true)

  if (isPending) {
    return (
      <Box className="sm:flex sm:justify-between">
        <Skeleton className="w-24 sm:mb-0 mb-1" variant="text" />
        <Skeleton className="w-24" variant="text" />
      </Box>
    )
  }

  if (error) {
    return (
      <StatusCard
        customIcon={<Illustration name="CloudLightning" color="error" />}
        headline={t('requestErrors.pageErrorIntro')}
      >
        <Description>
          {`${t('requestErrors.comeBackLater')} `}
          <TextAction isWindowReload text={t('requestErrors.refreshPage')} />.
        </Description>
      </StatusCard>
    )
  }

  return (
    <Box flex direction="col" gapY={4}>
      <Text variant="heading-3">{t('customers.customerDetails')}</Text>
      {isBusinessCustomer(customer) ? (
        <BusinessCustomerSidepanelView
          customer={customer}
          countries={countries}
          isCountriesLoading={isCountriesLoading}
          isCountriesLoadingError={isCountriesLoadingError}
        />
      ) : (
        <IndividualCustomerSidePanelView
          customer={customer}
          countries={countries}
          isCountriesLoading={isCountriesLoading}
          isCountriesLoadingError={isCountriesLoadingError}
        />
      )}
    </Box>
  )
}
